import { createReducer, on } from '@ngrx/store';
import { PigSigLookup } from 'src/app/models/lookup.model';
import { PigSigExtendedProduct, PigSigWallThickness, SpareParts } from 'src/app/models/pig-sig-selector.model';
import * as fromPigSig from '../fromPigSig.barrel';


export const pigsigFeatureKey = 'pigSig';



export enum Loaders {
  loadIndicators = 'Loading Indicators...',
  loadShafts = "Loading Shafts...",
  loadPlugs = "Loading Plugs...",
  loadOrings = "Loading Orings...",
  loadConnections = "Loading Connections...",
  loadFlangeMatls = "Loading Flange Materials...",
  loadDiameters = "Loading Diameters...",
  loadThicknesses = "Loading Wall Thicknesses",
  loadPigSigResults = "Loading Products...",
  loadSpareParts = "Loading Spare Parts",
  loadLookups = "loadLookups",
  loadDwg = 'loadDWG'
}

export interface PigSigLookups {
  indicators: PigSigLookup[];
  connections: PigSigLookup[];
  flangeMatls: PigSigLookup[];
  flangeHeights: PigSigFlangeHeight[];
  shafts: PigSigLookup[];
  plugs: PigSigLookup[];
  orings: PigSigLookup[];
  diameters: PigSigLookup[];
  wallThicknesses: PigSigWallThickness[];
  spareParts: SpareParts[];
}

export enum ConnectionType {
  TOR = 1,
  FLANGE = 2
}

export interface PigSigFilter {
  hasEnclosure?: boolean;
  indicatorID?: number;
  connectionID?: number;
  flangeMatlID?: number;
  flangeHeight?: number;
  diameterID?: number;
  shaftID?: number;
  plugID?: number;
  oringID?: number;
  wallThicknessID?: number;
  flangeConnLTEFour?: boolean;
  noTOR?: boolean;
}

export interface PigSigFlangeHeight {
  pipeSizePNVal: string;
  minHeight: number;
  maxHeight: number;
  partNumberValue: string;
}

export interface PigSigQueryResult {
  totalCount: number;
  products: PigSigExtendedProduct[]
}

export interface State {
  loaders: Loaders[];
  lookups: PigSigLookups;
  activeFilter: PigSigFilter;
  results?: PigSigQueryResult;
  activePartNumber?: string;
  activeFlangeHeights: PigSigFlangeHeight[];
}
export const initialState: State = {
  loaders: [],
  lookups: {
    indicators: [],
    connections: [],
    flangeMatls: [],
    flangeHeights: [],
    shafts: [],
    plugs: [],
    orings: [],
    diameters: [],
    wallThicknesses: [],
    spareParts: []
  },
  activeFilter: {},
  activeFlangeHeights: []
}

export const reducer = createReducer(
  initialState,
  on(fromPigSig.loadLookups, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadLookups)
  })),
  on(fromPigSig.updatePigSigLookups, (state, action) => ({
    ...state,
    lookups: {
      ...state.lookups,
      ...action.value
    }
  })),
  on(fromPigSig.loadIndicators, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadIndicators)
  })),

  on(fromPigSig.loadIndicatorsSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadIndicators)
  })),
  on(fromPigSig.loadIndicatorsFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadIndicators)
  })),

  on(fromPigSig.updatePigSigFilter, (state, action) => {

    // if the connection is a Flange, we want to remove values for diameter and thickness dropdowns
    if ((action.value.connectionID || -1) > 1) {
      return {
        ...state,
        activeFilter: {
          ...state.activeFilter,
          ...action.value,
          diameterID: undefined,
          wallThicknessID: undefined, // reset the wall thickness
          noTOR: undefined // reset the noTOR option
        }
      }
    }

    // if we are change the diameter we may need to reset the wall thickness selection
    const dia = state.lookups.diameters.find(d => d.id === action.value.diameterID);
    if (!!dia) {
      const wt = state.lookups.wallThicknesses.find(w => w.id === state.activeFilter.wallThicknessID);
      if (!!wt && (wt.diameterId !== dia.id)) {
        return {
          ...state,
          activeFlangeHeights: (action.value.connectionID || -1) === ConnectionType.TOR ? [] : state.activeFlangeHeights,
          activeFilter: {
            ...state.activeFilter,
            ...action.value,
            // flangeConnLTEFour: (action.value.connectionID || -1) > 1 ? action.value.flangeConnLTEFour || state.activeFilter.flangeConnLTEFour : false,
            wallThicknessID: undefined // reset the wall thickness
          }
        }
      }
    } // otherwise just set the properties like normal

    return {
      ...state,
      activeFilter: {
        ...state.activeFilter,
        ...action.value, // only send the properties you want to update
      }
    }
  }),
  on(fromPigSig.clearPigSigFilter, (state, action) => ({
    ...state,
    activeFilter: {},
    results: undefined,
    activePartNumber: undefined
  })),
  on(fromPigSig.loadPigSigResults, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadPigSigResults)
  })),
  on(fromPigSig.loadPigSigResultsSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadPigSigResults),
    results: action.pigSigResults
  })),
  on(fromPigSig.loadPigSigResultsFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadPigSigResults)
  })),
  on(fromPigSig.removeLoader, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== action.loader)
  })),
  on(fromPigSig.loadDwg, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadDwg)
  })),
  on(fromPigSig.loadDwgSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadDwg)
  })),
  on(fromPigSig.loadDwgFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadDwg)
  })),
  on(fromPigSig.loadFlangeHeightsSuccess, (state, action) => ({
    ...state,
    activeFlangeHeights: action.flangeHeights
  }))
  // removing this for now...
  // on(fromPigSig.activatePartNumber, (state, action) => ({
  //   ...state,
  //   activePartNumber: action.partnumber === state.activePartNumber ? undefined : action.partnumber
  // }))
)
